<template>
  <div>
    <b-form @submit.prevent="submitForm">
      <div class="d-flex flex-row flex-wrap align-items-center justify-content-between mb-2">
        <label class="m-0" for="inputSendToAddress">Send to address</label>
        <div class="text-primary">
          <span class="cursor-pointer mr-4" v-b-modal:address-book-modal-2><b-icon-bookmark/> Address book</span>
          <span class="cursor-pointer" v-b-modal:save-wallet-modal-2><b-icon-plus-lg/> Save new</span>
        </div>
      </div>
      <v-select class="vs-normalizer mb-3" :hidden="!!destReceiverLabel"
                label="label"
                :getOptionKey="s => s.address + ':' + s.memo"
                :filter="destReceiverSelectDropdownFilter"
                :options="savedWallets"
                :clearSearchOnBlur="() => false"
                v-on:option:selecting="onPickSavedWallet">
        <template v-slot:search="{attributes, events}">
          <input v-bind="attributes" v-on="events"
                 :value="destReceiver"
                 @input="destReceiver = $event.target.value"
                 @blur="destReceiver = destReceiver?.trim() || ''"
                 class="form-control text-monospace" required minlength="2" maxlength="100"/>
        </template>
        <template v-slot:option="{ label, address }">
          <div class="py-1">
            <div>{{ label }}</div>
            <div class="text-secondary text-monospace text-break">{{ address }}</div>
          </div>
        </template>
      </v-select>
      <div v-if="destReceiverLabel" class="border rounded d-flex flex-row align-items-center mb-3 p-2">
        <div class="flex-grow-1 min-width-0">
          <div class="overflow-hidden text-overflow-ellipsis">{{ destReceiverLabel }}</div>
          <div class="text-monospace">
            <a class="text-break" :href="destReceiverExplorerUrl" target="_blank">{{ destReceiver }}</a>
          </div>
        </div>
        <div class="flex-shrink-0">
          <b-button variant="link" class="text-secondary" @click="clearDestReceiver"><b-icon-x-circle/></b-button>
        </div>
      </div>
      <div class="d-flex flex-row align-items-center justify-content-between mb-1">
        <div>Amount</div>
        <div class="small text-secondary">
          <span class="cursor-pointer mr-2" @click="onClickBalance">Balance: {{ displayBalance }}</span>
          <b-icon-arrow-repeat class="cursor-pointer" @click="fetchBalances" />
        </div>
      </div>
      <b-form-group>
        <b-form-input type="number" :min="amountStep" :max="displayBalance" :step="amountStep" v-model="formInputAmount" required />
      </b-form-group>

      <div class="text-center">
        <b-button type="submit" variant="primary">Send</b-button>
      </div>
    </b-form>

    <b-modal id="address-book-modal-2" title="Address book" hide-footer no-fade no-close-on-backdrop @hidden="reloadSavedWallets">
      <AddressBookModal type="evm" @pickWallet="onPickSavedWallet"></AddressBookModal>
    </b-modal>
    <b-modal id="save-wallet-modal-2" title="Save new wallet" hide-footer no-fade no-close-on-backdrop @hidden="reloadSavedWallets">
      <SaveWalletModal modal-id="save-wallet-modal-2" :prefillAddress="destReceiver" type="evm" @done="onPickSavedWallet"></SaveWalletModal>
    </b-modal>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import SaveWalletModal from "@/components/SaveWalletModal.vue";
import AddressBookModal from "@/components/AddressBookModal.vue";
import * as utils from "@/utils";
import * as constants from "@/constants";
import * as savedWalletsService from "@/services/savedWalletsService";
import * as web3Service from "@/services/web3Service";
import * as onboardComposableExtra from "@/services/onboard";
import erc20Abi from "@/services/abi/erc20Abi.json";
import BigNumber from "bignumber.js";
import { ethers } from "ethers";

export default defineComponent({
  name: "SendTokenModal",
  components: {SaveWalletModal, AddressBookModal},
  inject: ["toastError", "toastSuccess", "toastSuccessDelay", "showLoading", "hideLoading"],
  props: ["modalId", "tokenInfo"],

  data() {
    return {
      destReceiverLabel: "",
      destReceiver: "",
      savedWallets: [],

      displayBalance: "0",
      formInputAmount: "",
    };
  },

  computed: {
    amountStep() {
      return BigNumber(1).shiftedBy(-this.tokenInfo.decimals).toNumber();
    },
    destReceiverExplorerUrl() {
      return utils.getExplorerUrl(this.tokenInfo.chainId, "address", this.destReceiver);
    },
  },

  mounted() {
    this.reloadSavedWallets();
    this.fetchBalances();
  },

  methods: {
    reloadSavedWallets() {
      this.savedWallets = savedWalletsService.getAllSavedWallets2("evm");
    },

    async fetchBalances() {
      const walletAddress = onboardComposableExtra.walletAddress.value;
      const balance = (await web3Service.getEvmTokenBalances(this.tokenInfo.chainId, walletAddress, [this.tokenInfo.contract]))[this.tokenInfo.contract.toLowerCase()];
      this.displayBalance = BigNumber(balance.toString()).shiftedBy(-this.tokenInfo.decimals).toFixed();
    },

    onClickBalance() {
      this.formInputAmount = this.displayBalance;
    },

    destReceiverSelectDropdownFilter(options: any[], search: string) {
      search = utils.sanitizeSearchText(search).toLowerCase();
      return options.filter(savedWallet =>
        savedWallet.label.toLowerCase().includes(search) ||
        savedWallet.address.toLowerCase().includes(search)
      );
    },

    onPickSavedWallet(savedWallet: savedWalletsService.SavedWallet) {
      this.destReceiver = savedWallet.address;
      this.destReceiverLabel = savedWallet.label;
      this.$bvModal.hide("address-book-modal-2");
    },

    clearDestReceiver() {
      this.destReceiverLabel = "";
      this.destReceiver = "";
    },

    async submitForm() {
      const amount = BigNumber(this.formInputAmount).shiftedBy(this.tokenInfo.decimals).toFixed();
      const signer = await onboardComposableExtra.browserProvider.value.getSigner();

      if (this.tokenInfo.contract.toLowerCase() === constants.NATIVE_ASSET_ADDRESS.toLowerCase()) {
        await signer.sendTransaction({
          to: this.destReceiver,
          value: amount
        });

      } else {
        const tokenContract = new ethers.Contract(this.tokenInfo.contract, erc20Abi, await web3Service.getProviderForChain(this.tokenInfo.chainId));
        const txRequest = await tokenContract.transfer.populateTransaction(this.destReceiver, amount);
        await signer.sendTransaction(txRequest);
      }
    }
  },


});
</script>
